"use client"

import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageLineTabs,
  PageHeaderActionSecondary,
  PageHeaderActions,
  useArtifactCanvas,
  parseSkillsForSmartSearch,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ChartNoAxesColumn, Users, Upload } from 'lucide-react'
import { RecruiterSearchPanel } from './recruiter-search-panel'
import { RecruiterTalentBrowseSection } from './recruiter-talent-browse-section'
import { CvUploadCanvasForm } from './cv-upload-canvas-form'

type TalentView = 'search' | 'talent'

function talentViewFromParams(searchParams: URLSearchParams): TalentView {
  const tab = searchParams.get('tab')
  if (tab === 'search') return 'search'
  return 'talent'
}

export function RecruiterTalentPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const briefIdFromUrl = searchParams.get('briefId')
  const activeView = talentViewFromParams(searchParams)
  const bridgedSkills = parseSkillsForSmartSearch(searchParams)
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const uploadCanvasOpenKeyRef = useRef(0)

  useEffect(() => {
    if (briefIdFromUrl) {
      const path = `/recruiter/briefs/${briefIdFromUrl}`
      const threadId = searchParams.get('thread')
      const fullPath = threadId ? `${path}?thread=${threadId}` : path
      navigate(fullPath, { replace: true })
    }
  }, [briefIdFromUrl, navigate, searchParams])

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const setActiveView = useCallback(
    (view: TalentView) => {
      const next = new URLSearchParams(searchParams)
      if (view === 'search') next.set('tab', 'search')
      else next.delete('tab')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const openUploadCanvas = useCallback(() => {
    uploadCanvasOpenKeyRef.current += 1
    openCanvas({
      title: 'Add to talent',
      preventAutoFocus: true,
      content: (
        <CvUploadCanvasForm
          key={`cv-upload-${uploadCanvasOpenKeyRef.current}`}
          onClose={closeCanvas}
        />
      ),
    })
  }, [closeCanvas, openCanvas])

  useEffect(() => {
    if (searchParams.get('upload') !== '1') return
    openUploadCanvas()
    const next = new URLSearchParams(searchParams)
    next.delete('upload')
    setSearchParams(next, { replace: true })
  }, [openUploadCanvas, searchParams, setSearchParams])

  const talentTabItems = useMemo(
    () => [
      { value: 'talent' as const, label: 'Talent', icon: Users },
      { value: 'search' as const, label: 'Analysis', icon: ChartNoAxesColumn },
    ],
    [],
  )

  return (
    <PageContainer>
      <div>
        <PageHeaderStack compact>
          <PageHeader
            leading={<PageHeaderLeading />}
            title="Talent"
            icon={<Users className="h-5 w-5" />}
            actions={
              activeView === 'talent' ? (
                <PageHeaderActions
                  secondary={
                    <PageHeaderActionSecondary type="button" onClick={openUploadCanvas}>
                      <Upload className="h-4 w-4" aria-hidden />
                      Upload files
                    </PageHeaderActionSecondary>
                  }
                />
              ) : null
            }
          />

          <PageLineTabs
            value={activeView}
            onValueChange={(value) => setActiveView(value as TalentView)}
            ariaLabel="Talent sections"
            items={talentTabItems}
          />
        </PageHeaderStack>

        {activeView === 'search' ? (
          <RecruiterSearchPanel mode="talent" initialSkillsFromBrowse={bridgedSkills} />
        ) : (
          <RecruiterTalentBrowseSection onUploadClick={openUploadCanvas} />
        )}
      </div>
    </PageContainer>
  )
}
